import React, { useMemo, useState } from 'react';

import { Alert, Center, Checkbox, Loader, Stack, Switch, Text, useMantineTheme } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import useAthlete from 'hooks/athlete/useAthlete';
import useAthletePackages from 'hooks/athletePackage/useAthletePackages';
import useGym from 'hooks/gym/useGym';
import useGymAthleteRetrieve from 'hooks/gym/useGymAthleteRetrieve';
import { AthletePackage } from 'types';

import AddPaymentMethodCS from './components/AddPaymentMethodCS';
import AddPaymentMethodPE from './components/AddPaymentMethodPE';
import { IAutoPayOnAttach } from './types';

// The statuses auto-pay can be enabled on, and so the ones that decide whether the toggle has
// anything to act on. Module-level so the query key stays stable across renders.
const CURRENT_PACKAGE_STATUSES: AthletePackage['status'][] = ['ACTIVE', 'UPCOMING', 'HOLD'];

interface IAddPaymentMethodProps {
  athleteId: string;
  gymId: string;
  cancelHandler: () => void;
  successHandler: (paymentMethodId?: string) => void;
  /**
   * Whether this form is being shown to gym staff. The auto-pay toggle is staff-only, and this
   * prop is the whole of that control: the flag reaches the backend as Stripe payment-method
   * metadata through a webhook that has no session and no role to check. It is left off by
   * default so the athlete-facing callers — the athlete Account page and the sales portal — stay
   * as they are without opting out.
   */
  isStaffContext?: boolean;
}

const AddPaymentMethod = ({
  athleteId,
  gymId,
  cancelHandler,
  successHandler,
  isStaffContext = false,
}: IAddPaymentMethodProps): JSX.Element => {
  const theme = useMantineTheme();
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [enableAutoPay, setEnableAutoPay] = useState(false);

  const { session } = useAuth();
  const isAthlete = session?.role === 'athlete';
  const staffGymQuery = useGym(isAthlete ? undefined : gymId);
  const athleteGymQuery = useGymAthleteRetrieve(isAthlete ? gymId : undefined);
  const {
    data: gymData,
    isLoading: isGymLoading,
    isError: isGymError,
  } = isAthlete ? athleteGymQuery : staffGymQuery;

  // Only fetched on the staff surfaces, since it is read for the toggle alone. On the payment
  // element route it costs nothing: AddPaymentMethodPE already holds this same query.
  const { data: athleteData, isSuccess: isAthleteLoaded } = useAthlete(
    athleteId,
    isStaffContext && !!athleteId,
  );

  const { data: athletePackagesData, isSuccess: isPackageCountLoaded } = useAthletePackages({
    athlete: athleteId,
    statuses: CURRENT_PACKAGE_STATUSES,
    // Only the count is read, so ask for the smallest page the endpoint will serve.
    limit: '1',
    enabled: isStaffContext && !!athleteId,
  });

  // A count that has not arrived, or failed to, hides the toggle rather than defaulting it on.
  // Its copy names exactly what it reaches, so offering it without knowing whether the member
  // holds any package would be promising a reach we cannot state. Staff get today's behaviour.
  const currentPackageCount = isPackageCountLoaded ? athletePackagesData?.pages[0]?.data.count ?? 0 : 0;

  // A card added on a subaccount's page attaches to the primary's Stripe customer, because
  // linking an account deletes the subaccount's own cards and nulls its customer id (see
  // Athlete.objects.independent_to_linked). The webhook resolves the athlete from that customer,
  // so the flag would land on the primary's packages rather than the ones counted above. Product
  // scoped this toggle to the athlete's own packages, so on that page there is nothing to offer.
  const isSubaccount = !!athleteData?.data.primaryAccount;

  const isCheckout = useMemo(
    () => gymData?.data.address.country === 'GB' && gymData?.data.stripeAccountType === 'standard',
    [gymData?.data.address.country, gymData?.data.stripeAccountType],
  );

  // The toggle only means something alongside the default card: the backend reads it inside the
  // set-as-default branch, so on its own it changes nothing. Hidden rather than disabled when
  // the member holds no current package, since there would be nothing for it to reach, and on a
  // subaccount's page, where it could not reach the packages this form is showing. An athlete
  // record that has not arrived, or failed to, hides it on the same ground the count does.
  const isAutoPayOffered =
    isStaffContext && setAsDefault && currentPackageCount > 0 && isAthleteLoaded && !isSubaccount;

  const autoPay: IAutoPayOnAttach | undefined = isAutoPayOffered
    ? { enabled: enableAutoPay, packageCount: currentPackageCount }
    : undefined;

  const defaultCheckbox = (
    <Checkbox
      px='xl'
      label={<Text fw={600}>Set as default payment method</Text>}
      description={
        <Text fs='italic' c='dimmed'>
          Will be used for future invoices
        </Text>
      }
      checked={setAsDefault}
      onChange={e => setSetAsDefault(e.currentTarget.checked)}
    />
  );

  const autoPayToggle = (
    <Switch
      px='xl'
      label={<Text fw={600}>Enable auto-pay for this member&apos;s packages</Text>}
      description={
        // Names what it actually reaches. Setting a default card spans linked accounts for the
        // card itself, and the auto-pay flag deliberately does not follow it, so this cannot say
        // "all packages".
        <Text fs='italic' c='dimmed'>
          Applies to their active, upcoming and on-hold packages and the upcoming invoices on them. Packages
          on linked accounts are not changed.
        </Text>
      }
      checked={enableAutoPay}
      onChange={e => setEnableAutoPay(e.currentTarget.checked)}
    />
  );

  // Both routes below render this same block, deliberately as one value rather than two repeated
  // lines. `isCheckout` picks a carrier for these options, not whether they are offered: it keys
  // off the gym — GB on a standard Connect account — so anything gated on it is withheld from a
  // whole class of gyms, staff drawer included, rather than from the athlete-facing surfaces the
  // toggle is meant to be off on. Which surface it is remains `isStaffContext`'s to say.
  const kiloOptions = (
    <>
      {defaultCheckbox}
      {isAutoPayOffered && autoPayToggle}
    </>
  );

  if (isGymLoading) {
    return (
      <Center my='xl'>
        <Loader />
      </Center>
    );
  }

  if (isGymError || !gymData?.data) {
    return (
      <Center my='xl'>
        <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
          Something went wrong!
        </Alert>
      </Center>
    );
  }

  if (isCheckout) {
    // The embedded checkout route carries both options through the checkout session's own
    // metadata rather than the payment method's. Different carrier, same pair of options.
    return (
      <Stack>
        {kiloOptions}
        <AddPaymentMethodCS
          gym={gymData.data}
          athleteId={athleteId}
          setAsDefault={setAsDefault}
          autoPay={autoPay}
          cancelHandler={cancelHandler}
          successHandler={successHandler}
        />
      </Stack>
    );
  }

  return (
    <Stack>
      {kiloOptions}
      <AddPaymentMethodPE
        gym={gymData.data}
        athleteId={athleteId}
        gymId={gymId}
        setAsDefault={setAsDefault}
        autoPay={autoPay}
        cancelHandler={cancelHandler}
        successHandler={successHandler}
      />
    </Stack>
  );
};

export default AddPaymentMethod;
